import { APP_ROUTES } from './constants/appRoutes';

/** postMessage source for the Google SSO popup callback page. */
export const GOOGLE_SSO_MESSAGE_SOURCE = 'wireless-portal-google-sso';

export type GoogleSsoMessage = {
  source: typeof GOOGLE_SSO_MESSAGE_SOURCE;
  state?: string;
  idToken?: string;
  error?: string;
};

export function getGoogleClientId(): string | undefined {
  const id = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim();
  return id || undefined;
}

export function getGoogleRedirectUri(): string {
  return `${window.location.origin}${APP_ROUTES.GOOGLE_CALLBACK}`;
}

function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

/** Decode JWT payload without verifying the signature (API verifies the Google ID token). */
function readJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isGoogleSsoMessage(data: unknown): data is GoogleSsoMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as GoogleSsoMessage).source === GOOGLE_SSO_MESSAGE_SOURCE
  );
}

/**
 * Obtain a Google OpenID ID token via popup for POST /api/auth/validate-google.
 * Redirects to a public callback page that postMessages the token to the opener
 * (avoids ProtectedRoute eating the OAuth hash on `/`).
 * Requires the callback URL in Google Cloud Console under Authorized redirect URIs
 * (and the SPA origin under Authorized JavaScript origins).
 * Requests the OIDC `amr` claim; Google only returns it when Authentication
 * strength claims is enabled for a published, verified OAuth client.
 */
export function requestGoogleIdToken(clientId: string): Promise<string> {
  const nonce = generateNonce();
  const state = generateNonce();
  const redirectUri = getGoogleRedirectUri();
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'id_token',
    scope: 'openid email',
    redirect_uri: redirectUri,
    nonce,
    state,
    prompt: 'select_account',
    claims: JSON.stringify({ id_token: { amr: { essential: true } } }),
  });

  const width = 500;
  const height = 640;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  const popup = window.open(
    `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`,
    'google-sso',
    `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no`
  );

  if (!popup) {
    return Promise.reject(
      new Error('Popup blocked. Please allow popups for this site and try Google sign-in again.')
    );
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let intervalId = 0;
    let timeoutId = 0;
    let closedGraceId = 0;
    const expectedOrigin = window.location.origin;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(closedGraceId);
      window.removeEventListener('message', onMessage);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      if (!isGoogleSsoMessage(event.data)) return;
      if (event.data.state !== state) return;

      try {
        popup.close();
      } catch {
        // ignore — COOP may block close from opener
      }

      if (event.data.error) {
        finish(() =>
          reject(
            new Error(
              event.data.error === 'access_denied'
                ? 'Google authentication cancelled.'
                : event.data.error === 'missing_id_token'
                  ? 'Google did not return an ID token. Please try again.'
                  : `Google sign-in failed (${event.data.error}).`
            )
          )
        );
        return;
      }

      const idToken = event.data.idToken;
      if (!idToken) {
        finish(() =>
          reject(new Error('Google did not return an ID token. Please try again.'))
        );
        return;
      }

      const payload = readJwtPayload(idToken);
      if (!payload || payload.nonce !== nonce) {
        finish(() =>
          reject(new Error('Google sign-in failed security check. Please try again.'))
        );
        return;
      }

      finish(() => resolve(idToken));
    };

    window.addEventListener('message', onMessage);

    timeoutId = window.setTimeout(() => {
      try {
        popup.close();
      } catch {
        // ignore
      }
      finish(() => reject(new Error('Google sign-in timed out. Please try again.')));
    }, 120_000);

    // Best-effort cancel detection. COOP may block `closed` while on Google.
    // After a successful callback, the popup closes itself — wait briefly so
    // postMessage can settle before treating close as cancel.
    intervalId = window.setInterval(() => {
      try {
        if (!popup.closed) return;
        window.clearInterval(intervalId);
        intervalId = 0;
        closedGraceId = window.setTimeout(() => {
          finish(() => reject(new Error('Google authentication cancelled.')));
        }, 750);
      } catch {
        // COOP / cross-origin — ignore until callback posts or timeout.
      }
    }, 500);
  });
}
